import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FlaskConical, Search, RefreshCw, User, Edit, Eye, Check, X,
  Clock, CheckCircle, Send, Loader2, Filter, Printer, Activity
} from 'lucide-react';
import { labResultsAPI, labTestsAPI } from '../../services/api';
import ResultsDetailsModal from '../../components/lab/ResultsDetailsModal';
import toast from 'react-hot-toast';

const LabResults = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;

  // Entry modal
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [bookingEntryTests, setBookingEntryTests] = useState([]);
  const [entryData, setEntryData] = useState({ components: [], result_comment: '' });
  const [saving, setSaving] = useState(false);
  const [loadingResult, setLoadingResult] = useState(false);
  const [cultureRows, setCultureRows] = useState([]);
  const [serialEntry, setSerialEntry] = useState([]);
  const [richTextHtml, setRichTextHtml] = useState('');
  const [richTextImages, setRichTextImages] = useState([]);
  const [cultureOrganisms, setCultureOrganisms] = useState([]);
  const [cultureAntibiotics, setCultureAntibiotics] = useState([]);

  // View modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);

  const statusOptions = [
    { value: '', label: 'جميع الحالات' },
    { value: 'pending', label: 'في الانتظار' },
    { value: 'completed', label: 'مكتمل' },
    { value: 'approved', label: 'معتمد' },
    { value: 'released', label: 'تم الإرسال' }
  ];

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sort_order: sortOrder,
      };
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (searchQuery) params.search = searchQuery;
      // Always group tests by booking on the listing page — one row = one booking.
      // Entry of individual test results is done through the booking detail view,
      // where each component is addressed inside the combined result form.
      params.group_by = 'booking';

      const response = await labResultsAPI.getAll(params);
      const data = response.data?.data || {};
      setResults(Array.isArray(data.items) ? data.items : []);
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.total || 0);
    } catch (err) {
      console.error('Error fetching results:', err);
      toast.error('حدث خطأ في تحميل النتائج');
      setResults([]);
      setTotalPages(1);
      setTotalItems(0);
    }
    setLoading(false);
  }, [currentPage, statusFilter, dateFrom, dateTo, sortOrder, searchQuery]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  useEffect(() => {
    const loadCultureMasters = async () => {
      try {
        const [oRes, aRes] = await Promise.all([
          labTestsAPI.getCultureOrganisms(),
          labTestsAPI.getCultureAntibiotics()
        ]);
        setCultureOrganisms(oRes.data?.data || []);
        setCultureAntibiotics(aRes.data?.data || []);
      } catch {
        setCultureOrganisms([]);
        setCultureAntibiotics([]);
      }
    };
    loadCultureMasters();
  }, []);

  // Calculate abnormal flag based on value and reference range
  const calculateAbnormalFlag = (value, refRange) => {
    if (!refRange || value === '' || value === null) return null;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return null;

    if (refRange.low_value !== null && numValue < refRange.low_value) return 'L';
    if (refRange.high_value !== null && numValue > refRange.high_value) return 'H';
    return 'N';
  };

  const getAbnormalLabel = (flag) => {
    switch (flag) {
      case 'H': return { text: 'High', color: 'text-red-600 bg-red-50' };
      case 'L': return { text: 'Low', color: 'text-red-600 bg-red-50' };
      case 'N': return { text: 'Normal', color: 'text-green-600 bg-green-50' };
      default: return null;
    }
  };

  const loadEntryForResult = async (result) => {
    setSelectedResult(result);
    setLoadingResult(true);
    try {
      const response = await labResultsAPI.getDetails(result.booking_test_id);
      const data = response.data?.data;

      const isCalculated = data?.bookingTest?.lab_test_id?.is_calculated || false;

      if (data?.components && data.components.length > 0) {
        setEntryData({
          components: data.components.map(c => ({
            ...c,
            value: c.value_numeric ?? c.value_text ?? '',
            abnormal_flag: c.abnormal_flag || null,
            is_calculated: isCalculated
          })),
          result_comment: data.result?.result_comment || ''
        });
        setCultureRows(data?.culture_grid_rows || data?.result?.culture_grid_rows || []);
        setRichTextHtml(data?.rich_text_html || data?.result?.rich_text_html || '');
        setRichTextImages(data?.rich_text_images || data?.result?.rich_text_images || []);

        const serialIntervals = result?.serial_intervals || [];
        const serialSamples = result?.serial_samples || [];
        const existingSerial = data?.serial_results || data?.result?.serial_results || [];
        if (result?.is_serial_test && serialIntervals.length > 0) {
          const baseComponents = data.components || [];
          const built = serialIntervals.map((label, idx) => {
            const sample = serialSamples[idx] || {};
            const existing = existingSerial.find((r) => r.label === label) || {};
            return {
              label,
              sample_id: existing.sample_id || sample.sample_id || null,
              sample_collected_at: existing.sample_collected_at || '',
              components: baseComponents.map((comp) => {
                const existingComp = (existing.components || []).find(
                  (ec) => String(ec.test_component_id) === String(comp.component_id || comp.test_component_id)
                );
                return {
                  test_component_id: comp.component_id || comp.test_component_id,
                  component_name: comp.component_name,
                  result_type: comp.result_type,
                  unit: comp.unit,
                  options: comp.options || [],
                  reference_range: comp.reference_range || null,
                  value: existingComp ? (existingComp.value_numeric ?? existingComp.value_text ?? '') : '',
                  abnormal_flag: existingComp?.abnormal_flag || null
                };
              })
            };
          });
          setSerialEntry(built);
        } else {
          setSerialEntry([]);
        }
      } else {
        setEntryData({
          components: [{
            component_id: null,
            component_name: result.test_name || 'النتيجة',
            unit: '',
            result_type: 'numeric',
            reference_range: null,
            value: '',
            abnormal_flag: null,
            is_calculated: isCalculated
          }],
          result_comment: data?.result?.result_comment || ''
        });
        setCultureRows([]);
        setSerialEntry([]);
        setRichTextHtml('');
        setRichTextImages([]);
      }
    } catch (err) {
      setEntryData({
        components: [{
          component_id: null,
          component_name: result.test_name || 'النتيجة',
          unit: '',
          result_type: 'numeric',
          reference_range: null,
          value: '',
          abnormal_flag: null,
          is_calculated: false
        }],
        result_comment: ''
      });
      setCultureRows([]);
      setSerialEntry([]);
      setRichTextHtml('');
      setRichTextImages([]);
    }
    setLoadingResult(false);
  };

  const handleOpenEntry = async (result) => {
    setShowEntryModal(true);
    const siblings = results
      .filter((r) => r.booking_id === result.booking_id)
      .sort((a, b) => String(a.test_name || '').localeCompare(String(b.test_name || '')));
    setBookingEntryTests(siblings);
    await loadEntryForResult(result);
  };

  const handleSaveResult = async () => {
    if (!selectedResult) return;

    if (selectedResult?.is_serial_test && serialEntry.length > 0) {
      const hasAnyValue = serialEntry.some((r) => (r.components || []).some((c) => c.value !== '' && c.value !== null && c.value !== undefined));
      if (!hasAnyValue) {
        toast.error('يرجى إدخال قيمة واحدة على الأقل في أحد فترات السحب');
        return;
      }
    }

    const validComponents = entryData.components.filter(c => c.value !== '' && c.value !== null);
    const hasCultureData = cultureRows.some((r) => r.organism_name && (r.antibiotics || []).length > 0);
    const hasRichTextData = richTextHtml && richTextHtml.trim();
    if (!selectedResult?.is_serial_test && validComponents.length === 0 && !hasCultureData && !hasRichTextData) {
      toast.error('يرجى إدخال قيمة واحدة على الأقل');
      return;
    }

    setSaving(true);
    try {
      await labResultsAPI.enterResult(selectedResult.booking_test_id, {
        components: validComponents.map(c => ({
          test_component_id: c.component_id || undefined,
          value_numeric: c.result_type === 'numeric' ? parseFloat(c.value) : null,
          value_text: c.result_type !== 'numeric' ? c.value : null,
          unit: c.unit || null,
          abnormal_flag: c.abnormal_flag || null
        })),
        serial_results: selectedResult?.is_serial_test ? serialEntry.map((row) => ({
          label: row.label,
          sample_id: row.sample_id || null,
          sample_collected_at: row.sample_collected_at || null,
          components: (row.components || [])
            .filter((c) => c.value !== '' && c.value !== null && c.value !== undefined)
            .map((c) => ({
              test_component_id: c.test_component_id,
              value_numeric: c.result_type === 'numeric' ? parseFloat(c.value) : null,
              value_text: c.result_type !== 'numeric' ? c.value : null,
              unit: c.unit || null,
              abnormal_flag: c.abnormal_flag || null
            }))
        })) : [],
        culture_grid_rows: cultureRows,
        rich_text_html: richTextHtml || null,
        rich_text_images: richTextImages,
        result_comment: entryData.result_comment
      });
      toast.success('تم حفظ النتيجة بنجاح');
      setShowEntryModal(false);
      setBookingEntryTests([]);
      fetchResults();
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
    }
    setSaving(false);
  };

  const handleOpenView = async (result) => {
    try {
      const response = await labResultsAPI.getBookingResults(result.booking_id);
      setViewData(response.data?.data);
      setShowViewModal(true);
    } catch (err) {
      toast.error('حدث خطأ في تحميل النتائج');
    }
  };

  const statusConfig = {
    pending: { label: 'في الانتظار', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
    completed: { label: 'مكتمل', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', dot: 'bg-sky-500' },
    approved: { label: 'معتمد', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
    released: { label: 'تم الإرسال', bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', dot: 'bg-slate-500' }
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide ${config.bg} ${config.text} border ${config.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
        {config.label}
      </span>
    );
  };

  const getPatientName = (result) => {
    return result.patient_name || '-';
  };

  const addCultureRow = () => {
    setCultureRows((prev) => [...prev, { organism_name: '', antibiotics: [] }]);
  };

  const addCultureAntibiotic = (rowIndex) => {
    setCultureRows((prev) => prev.map((row, idx) => (
      idx === rowIndex
        ? { ...row, antibiotics: [...(row.antibiotics || []), { antibiotic_name: '', sensitivity: 'S', mic_value: '' }] }
        : row
    )));
  };

  const onRichImageUpload = async (files) => {
    const mapped = await Promise.all(Array.from(files || []).map((file) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ url: reader.result, caption: file.name });
      reader.readAsDataURL(file);
    })));
    setRichTextImages((prev) => [...prev, ...mapped]);
  };

  const pendingCount = results.filter(r => r.status === 'pending').length;
  const completedCount = results.filter(r => r.status === 'completed').length;

  const hasActiveFilters = statusFilter || dateFrom || dateTo || sortOrder !== 'asc';
  const visiblePages = (() => {
    const total = Math.max(totalPages, 1);
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(total, start + 4);
    const adjustedStart = Math.max(1, end - 4);
    return Array.from({ length: end - adjustedStart + 1 }, (_, i) => adjustedStart + i);
  })();

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
              <FlaskConical className="w-6 h-6 text-slate-100" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">نتائج التحاليل</h1>
              <p className="text-slate-500 text-sm">إدخال وعرض نتائج التحاليل</p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-3">
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-center min-w-[80px]">
              <p className="text-2xl font-bold text-amber-700 tabular-nums">{pendingCount}</p>
              <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">في الانتظار</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2 text-center min-w-[80px]">
              <p className="text-2xl font-bold text-emerald-700 tabular-nums">{completedCount}</p>
              <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">مكتمل</p>
            </div>
          </div>
        </motion.div>

        {/* Search & Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] mb-6"
        >
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  placeholder="البحث بالباركود أو اسم المريض..."
                  className="w-full pr-10 pl-4 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${showFilters || hasActiveFilters
                  ? 'bg-[#1E293B] text-slate-100'
                  : 'bg-white border border-[#1E293B]/20 text-[#1E293B] hover:bg-slate-50'
                  }`}
              >
                <Filter className="w-4 h-4 text-blue-600" strokeWidth={2} />
                <span className="hidden sm:inline">فلترة</span>
              </button>
              <button
                onClick={fetchResults}
                className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all duration-200 flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={2} />
                <span className="hidden sm:inline">تحديث</span>
              </button>
            </div>
          </div>

          {/* Expandable Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="p-4 bg-slate-50/50 border-t border-slate-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الحالة</label>
                      <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all appearance-none cursor-pointer"
                      >
                        {statusOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الترتيب</label>
                      <select
                        value={sortOrder}
                        onChange={(e) => { setSortOrder(e.target.value); setCurrentPage(1); }}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all appearance-none cursor-pointer"
                      >
                        <option value="asc">من الأقدم إلى الأحدث</option>
                        <option value="desc">من الأحدث إلى الأقدم</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">من تاريخ</label>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">إلى تاريخ</label>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all"
                      />
                    </div>
                  </div>
                  {hasActiveFilters && (
                    <div className="mt-3 pt-3 border-t border-slate-200/60">
                      <button
                        onClick={() => { setStatusFilter(''); setSortOrder('asc'); setDateFrom(''); setDateTo(''); setCurrentPage(1); }}
                        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                        مسح الفلاتر
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Results Table */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_4px_6px_-1px_rgb(0_0_0_/_0.05)] overflow-hidden"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin mb-3" />
              <p className="text-sm text-slate-500">جاري تحميل النتائج...</p>
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <FlaskConical className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">لا توجد نتائج</h3>
              <p className="text-sm text-slate-500">لم يتم العثور على نتائج مطابقة</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-right px-6 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الباركود</th>
                    <th className="text-right px-6 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">اسم المريض</th>
                    <th className="text-right px-6 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">التحليل</th>
                    <th className="text-right px-6 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الفرع</th>
                    <th className="text-center px-6 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الحالة</th>
                    <th className="text-center px-6 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => (
                    <motion.tr
                      key={result.booking_test_id || result.booking_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                      className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150"
                    >
                      <td className="px-6 py-5">
                        <span className="font-mono text-sm font-bold text-slate-900 bg-slate-100 px-2.5 py-1 rounded-lg">
                          {result.barcode_no}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-slate-600">
                              {getPatientName(result)?.charAt(0) || '؟'}
                            </span>
                          </div>
                          <span className="font-semibold text-slate-900 text-sm">{getPatientName(result)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-2">
                          <FlaskConical className="w-4 h-4 text-slate-400" strokeWidth={2} />
                          <span className="text-sm text-slate-700">
                            {result.test_name
                              || (result.tests_count ? `${result.tests_count} تحليل في الحجز` : '-')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-sm text-slate-600">{result.branch_name || '-'}</td>
                      <td className="px-6 py-5 text-center">{getStatusBadge(result.status)}</td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleOpenView(result)}
                            className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                            title="عرض"
                          >
                            <Eye className="w-4 h-4" strokeWidth={2} />
                          </button>
                          {(result.status === 'completed' || result.status === 'approved' || result.status === 'released') && (
                            <button
                              onClick={() => navigate(`/lab/report/${result.booking_id}`)}
                              className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-150"
                              title="طباعة التقرير"
                            >
                              <Printer className="w-4 h-4" strokeWidth={2} />
                            </button>
                          )}
                          {result.booking_test_id && result.status !== 'released' && (
                            <button
                              onClick={() => handleOpenEntry(result)}
                              className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                              title="إدخال/تعديل النتيجة"
                            >
                              <Edit className="w-4 h-4" strokeWidth={2} />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
                </table>
              </div>
              <div className="px-4 sm:px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50/30">
                <p className="text-sm text-slate-600 order-2 sm:order-1">
                  عرض <span className="font-semibold text-slate-900">{totalItems === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1}</span>
                  {' - '}
                  <span className="font-semibold text-slate-900">{Math.min(currentPage * itemsPerPage, totalItems)}</span>
                  {' '}من <span className="font-semibold text-slate-900">{totalItems}</span>
                </p>
                <div className="flex items-center gap-2 order-1 sm:order-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all text-sm"
                  >
                    السابق
                  </button>
                  <div className="flex items-center gap-1">
                    {visiblePages.map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 rounded-lg text-xs font-medium transition-all ${
                          currentPage === pageNum
                            ? 'bg-slate-900 text-white'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(Math.max(totalPages, 1), p + 1))}
                    disabled={currentPage >= totalPages}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all text-sm"
                  >
                    التالي
                  </button>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>

      {/* Entry Modal */}
      <AnimatePresence>
        {showEntryModal && selectedResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => { setShowEntryModal(false); setBookingEntryTests([]); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-4xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-[#1E293B] px-6 py-5 flex items-center justify-between flex-shrink-0">
                <div>
                  <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                    <FlaskConical className="w-5 h-5" strokeWidth={2} />
                    إدخال نتيجة التحليل
                  </h3>
                  <p className="text-slate-400 text-sm mt-1">
                    {selectedResult.test_name} - <span className="font-mono">{selectedResult.barcode_no}</span>
                  </p>
                </div>
                <button onClick={() => { setShowEntryModal(false); setBookingEntryTests([]); }} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-slate-100" strokeWidth={2} />
                </button>
              </div>

              {bookingEntryTests.length > 1 && (
                <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 flex flex-wrap gap-2">
                  {bookingEntryTests.map((t) => {
                    const active = selectedResult?.booking_test_id === t.booking_test_id;
                    return (
                      <button
                        key={t.booking_test_id}
                        onClick={() => loadEntryForResult(t)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          active ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        {t.test_name || '-'}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-6 space-y-4" dir="rtl">
                {loadingResult ? (
                  <div className="flex items-center justify-center py-16">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                    <span className="mr-3 text-gray-500">جاري تحميل البيانات...</span>
                  </div>
                ) : (
                  <>
                    {selectedResult?.is_serial_test && serialEntry.length > 0 && (
                      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
                        <h3 className="font-bold text-slate-800">نتائج التحليل المتسلسل</h3>
                        {serialEntry.map((row, rowIdx) => (
                          <div key={`${row.label}-${rowIdx}`} className="border border-slate-200 rounded-xl p-3">
                            <div className="flex flex-wrap items-center gap-3 mb-3">
                              <span className="px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-semibold">{row.label}</span>
                              {row.sample_id && <span className="text-xs font-mono text-slate-500">{row.sample_id}</span>}
                              <input
                                type="datetime-local"
                                value={row.sample_collected_at ? new Date(row.sample_collected_at).toISOString().slice(0, 16) : ''}
                                onChange={(e) => {
                                  setSerialEntry((prev) => prev.map((x, i) => (i === rowIdx ? { ...x, sample_collected_at: e.target.value } : x)));
                                }}
                                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                              />
                            </div>
                            <div className="space-y-2">
                              {(row.components || []).map((comp, cIdx) => (
                                <div key={`${rowIdx}-${cIdx}`} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
                                  <span className="text-sm text-slate-700">{comp.component_name}</span>
                                  {comp.result_type === 'single_select' || comp.result_type === 'multi_select' ? (
                                    <select
                                      value={comp.value || ''}
                                      onChange={(e) => {
                                        setSerialEntry((prev) => prev.map((x, i) => {
                                          if (i !== rowIdx) return x;
                                          const comps = [...x.components];
                                          comps[cIdx] = { ...comps[cIdx], value: e.target.value };
                                          return { ...x, components: comps };
                                        }));
                                      }}
                                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                                    >
                                      <option value="">اختر...</option>
                                      {(comp.options || []).map((opt, i) => (
                                        <option key={i} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      type={comp.result_type === 'numeric' ? 'number' : 'text'}
                                      value={comp.value || ''}
                                      onChange={(e) => {
                                        setSerialEntry((prev) => prev.map((x, i) => {
                                          if (i !== rowIdx) return x;
                                          const comps = [...x.components];
                                          comps[cIdx] = { ...comps[cIdx], value: e.target.value };
                                          return { ...x, components: comps };
                                        }));
                                      }}
                                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                    />
                                  )}
                                  <span className="text-xs text-slate-500">{comp.unit || '-'}</span>
                                  <span className="text-xs text-slate-400">{comp.reference_range ? `${comp.reference_range.low_value} - ${comp.reference_range.high_value}` : '-'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {entryData.components.some((c) => c.result_type === 'culture_grid') && (
                      <div className="bg-white rounded-xl border border-emerald-200 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-emerald-800">Culture Grid</h3>
                          <button type="button" onClick={addCultureRow} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs">+ إضافة Organism</button>
                        </div>
                        {cultureRows.map((row, rowIdx) => (
                          <div key={rowIdx} className="border border-emerald-100 rounded-lg p-3">
                            <div className="mb-2">
                              <select
                                value={row.organism_name || ''}
                                onChange={(e) => setCultureRows((prev) => prev.map((x, i) => (i === rowIdx ? { ...x, organism_name: e.target.value } : x)))}
                                className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm"
                              >
                                <option value="">اختر Organism</option>
                                {cultureOrganisms.map((o) => <option key={o._id} value={o.name}>{o.name}</option>)}
                              </select>
                            </div>
                            <div className="space-y-2">
                              {(row.antibiotics || []).map((ab, abIdx) => (
                                <div key={abIdx} className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                  <select
                                    value={ab.antibiotic_name || ''}
                                    onChange={(e) => setCultureRows((prev) => prev.map((x, i) => {
                                      if (i !== rowIdx) return x;
                                      const antibiotics = [...(x.antibiotics || [])];
                                      antibiotics[abIdx] = { ...antibiotics[abIdx], antibiotic_name: e.target.value };
                                      return { ...x, antibiotics };
                                    }))}
                                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                  >
                                    <option value="">Antibiotic</option>
                                    {cultureAntibiotics.map((a) => <option key={a._id} value={a.name}>{a.name}</option>)}
                                  </select>
                                  <select
                                    value={ab.sensitivity || 'S'}
                                    onChange={(e) => setCultureRows((prev) => prev.map((x, i) => {
                                      if (i !== rowIdx) return x;
                                      const antibiotics = [...(x.antibiotics || [])];
                                      antibiotics[abIdx] = { ...antibiotics[abIdx], sensitivity: e.target.value };
                                      return { ...x, antibiotics };
                                    }))}
                                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                  >
                                    <option value="S">Sensitive (S)</option>
                                    <option value="I">Intermediate (I)</option>
                                    <option value="R">Resistant (R)</option>
                                  </select>
                                  <input
                                    value={ab.mic_value || ''}
                                    onChange={(e) => setCultureRows((prev) => prev.map((x, i) => {
                                      if (i !== rowIdx) return x;
                                      const antibiotics = [...(x.antibiotics || [])];
                                      antibiotics[abIdx] = { ...antibiotics[abIdx], mic_value: e.target.value };
                                      return { ...x, antibiotics };
                                    }))}
                                    placeholder="MIC"
                                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                  />
                                </div>
                              ))}
                              <button type="button" onClick={() => addCultureAntibiotic(rowIdx)} className="px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-lg text-xs">
                                + إضافة Antibiotic
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {entryData.components.some((c) => c.result_type === 'rich_text_with_images') && (
                      <div className="bg-white rounded-xl border border-violet-200 p-4 space-y-3">
                        <h3 className="font-bold text-violet-800">Rich Text Report</h3>
                        <div className="flex gap-2">
                          {['bold', 'italic', 'insertUnorderedList', 'insertOrderedList'].map((cmd) => (
                            <button key={cmd} type="button" onClick={() => document.execCommand(cmd)} className="px-2 py-1 border border-slate-200 rounded text-xs">
                              {cmd}
                            </button>
                          ))}
                        </div>
                        <div
                          contentEditable
                          suppressContentEditableWarning
                          className="min-h-[140px] border border-slate-200 rounded-lg p-3 text-sm"
                          onInput={(e) => setRichTextHtml(e.currentTarget.innerHTML)}
                          dangerouslySetInnerHTML={{ __html: richTextHtml || '' }}
                        />
                        <input type="file" multiple accept="image/*" onChange={(e) => onRichImageUpload(e.target.files)} />
                        {richTextImages.length > 0 && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {richTextImages.map((img, idx) => (
                              <img key={idx} src={img.url} alt={img.caption || `img-${idx}`} className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {entryData.components.filter((comp) => !['culture_grid', 'rich_text_with_images'].includes(comp.result_type)).length > 0 && (
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <div className="bg-gradient-to-l from-indigo-50 to-blue-50 px-5 py-3 border-b border-slate-200">
                          <div className="flex items-center justify-between">
                            <h3 className="font-bold text-indigo-800 flex items-center gap-2">
                              <FlaskConical className="w-4 h-4" />
                              المكونات
                            </h3>
                          </div>
                        </div>

                        <div className="divide-y divide-slate-100">
                          {entryData.components.filter((comp) => !['culture_grid', 'rich_text_with_images'].includes(comp.result_type)).map((comp, compIdx) => {
                          const abnormalFlag = calculateAbnormalFlag(comp.value, comp.reference_range);
                          const abnormalLabel = getAbnormalLabel(abnormalFlag);

                          return (
                            <div key={comp.component_id || compIdx} className="px-5 py-3 hover:bg-slate-50/50 transition-colors">
                              <div className="flex items-center gap-4" dir="ltr">
                                <div className="w-48 flex-shrink-0">
                                  <p className="font-semibold text-slate-700 text-sm">{comp.component_name}</p>
                                  {comp.unit && <p className="text-xs text-slate-400">{comp.unit}</p>}
                                </div>

                                <div className="flex-1 relative">
                                  {comp.result_type === 'single_select' || comp.result_type === 'multi_select' ? (
                                    <select
                                      value={comp.value || ''}
                                      onChange={(e) => {
                                        const newComps = [...entryData.components];
                                        newComps[compIdx].value = e.target.value;
                                        newComps[compIdx].abnormal_flag = calculateAbnormalFlag(e.target.value, comp.reference_range);
                                        setEntryData({ ...entryData, components: newComps });
                                      }}
                                      disabled={comp.is_calculated}
                                      className={`w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none ${comp.is_calculated ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''
                                        }`}
                                    >
                                      <option value="">اختر نتيجة</option>
                                      {(comp.options || []).map((opt, i) => (
                                        <option key={i} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      type={comp.result_type === 'numeric' ? 'number' : 'text'}
                                      value={comp.value || ''}
                                      onChange={(e) => {
                                        const newComps = [...entryData.components];
                                        newComps[compIdx].value = e.target.value;
                                        newComps[compIdx].abnormal_flag = calculateAbnormalFlag(e.target.value, comp.reference_range);
                                        setEntryData({ ...entryData, components: newComps });
                                      }}
                                      disabled={comp.is_calculated}
                                      placeholder={comp.is_calculated ? 'يتم حسابه آلياً' : 'أدخل القيمة'}
                                      className={`w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${comp.is_calculated ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''
                                        }`}
                                    />
                                  )}

                                  {comp.is_calculated && (
                                    <div className="absolute left-2 top-2.5 text-blue-500" title="قيمة محسوبة آلياً">
                                      <Activity className="w-4 h-4" />
                                    </div>
                                  )}
                                </div>

                                <div className="w-36 text-center flex-shrink-0">
                                  {comp.reference_range ? (
                                    <p className="text-xs text-slate-500 bg-slate-50 px-2 py-1.5 rounded-lg">
                                      {comp.reference_range.low_value} - {comp.reference_range.high_value}
                                    </p>
                                  ) : (
                                    <p className="text-xs text-slate-300">-</p>
                                  )}
                                </div>

                                <div className="w-20 text-center flex-shrink-0">
                                  {abnormalLabel ? (
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${abnormalLabel.color}`}>
                                      {abnormalLabel.text}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-slate-300">-</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">ملاحظات إضافية (اختياري)</label>
                      <textarea
                        value={entryData.result_comment}
                        onChange={(e) => setEntryData(prev => ({ ...prev, result_comment: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        rows={2}
                        placeholder="أي ملاحظات على النتيجة..."
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 flex-shrink-0">
                <button
                  onClick={() => { setShowEntryModal(false); setBookingEntryTests([]); }}
                  className="px-5 py-2.5 text-slate-700 bg-white border border-slate-200 rounded-xl font-medium hover:bg-slate-50 transition-colors"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSaveResult}
                  disabled={saving || loadingResult}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />جاري الحفظ...</>
                  ) : (
                    <><Check className="w-4 h-4" strokeWidth={2.5} />حفظ النتيجة</>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ResultsDetailsModal
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        data={viewData}
        title="عرض نتائج الحجز"
      />
    </div>
  );
};

export default LabResults;
